import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const courseProfiles = [
  { name: '习思想概论', indicator: '理论政策联系', color: 'primary' as const },
  { name: '原理', indicator: '概念论证', color: 'accent' as const },
  { name: '纲要', indicator: '历史逻辑', color: 'primary' as const },
  { name: '德法', indicator: '问题回应', color: 'accent' as const },
  { name: '毛中特概论', indicator: '体系发展', color: 'primary' as const },
];

const crossCourseDims = [
  '课堂结构',
  '案例使用',
  '互动质量',
  '节奏管理',
  '教学资源',
  '学生反馈',
];

export default function P72SchoolMap({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-4 md:py-6">
        <div ref={titleRef} className="text-center mb-3 md:mb-4">
          <span className={`inline-flex items-center gap-2 text-xs font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />一校一图
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            先分课程评价，再看<span className="text-gold-600">跨课程共性</span>
          </h2>
          <p className={`text-sm text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            课程专业性与学校整体治理并不冲突，关键是先分课程判断，再跨课程汇总
          </p>
        </div>

        {/* Upper layer: Course Profiles */}
        <div className={`max-w-5xl mx-auto mb-3 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="p-4 md:p-5 glass-liquid rounded-xl">
            <div className="text-center mb-3">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-body font-bold bg-primary-100/60 text-primary-700 border border-primary-200/40">
                第一层：各课程保留自己的专业指标
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-3">
              {courseProfiles.map((c, i) => (
                <div
                  key={c.name}
                  className={`p-3 rounded-xl text-center border ${
                    c.color === 'accent'
                      ? 'bg-gold-100/60 border-gold-200/50'
                      : 'bg-primary-100/30 border-primary-200/40'
                  }`}
                  style={{ transitionDelay: `${300 + i * 100}ms` }}
                >
                  <h4 className={`text-xs font-body font-bold mb-1 ${c.color === 'accent' ? 'text-gold-700' : 'text-primary-700'}`}>
                    {c.name}
                  </h4>
                  <p className="text-[10px] text-foreground-500 font-body">{c.indicator}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Lower layer: Cross-Course */}
        <div className={`max-w-5xl mx-auto mb-3 transition-all duration-1000 delay-400 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="p-4 md:p-5 glass-liquid rounded-xl">
            <div className="text-center mb-3">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-body font-bold bg-gold-100/80 text-gold-700 border border-gold-300/40">
                第二层：跨课程共性分析
              </span>
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              {crossCourseDims.map((dim, i) => (
                <span
                  key={dim}
                  className="px-3 py-1.5 rounded-full text-xs font-body font-semibold bg-secondary-100/60 text-secondary-700 border border-secondary-200/30"
                  style={{ transitionDelay: `${400 + i * 80}ms` }}
                >
                  {dim}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Center Conclusion */}
        <div className={`max-w-2xl mx-auto mb-3 transition-all duration-1000 delay-600 ${titleVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <div className="p-4 rounded-xl glass-gold border border-gold-200/40 text-center">
            <p className="font-heading text-sm md:text-base font-bold text-gold-800">
              课程内评价专业质量，课程间分析共性问题
            </p>
          </div>
        </div>

        <div className={`max-w-3xl mx-auto text-center transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-4 glass-liquid rounded-xl text-center">
            <p className="text-sm text-foreground-600 font-body leading-relaxed">
              一校一图既尊重每门课程的专业特点，又能够发现学校共同短板。<strong className="text-gold-700">管理者可以从全校共性问题下钻到某门课程、某位教师和某一段课堂证据。</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}